'use client';
import { useTranslation } from '../../lib/i18n';
import { updateOperationTitle, type UpdateDockOperation } from '../../lib/operationDockUpdates';
import { updateDisplayLabel } from '../../lib/updateStatus';
import { Button } from './Button';
import { Modal, ModalBody, ModalFooter } from './Modal';
import { Progress } from './shadcn/progress';
import { Spinner } from './states';

interface UpdateOperationProgressDialogProps {
  operation: UpdateDockOperation;
  onClose: () => void;
  onClosed?: () => void;
}

/** Durable update progress remains in the dock when this shared modal is minimized. */
export function UpdateOperationProgressDialog({ operation, onClose, onClosed }: UpdateOperationProgressDialogProps) {
  const { t } = useTranslation();
  const running = operation.status === 'running';
  const failed = operation.status === 'failed';
  const label = updateDisplayLabel(operation.state, t.updateStatus);
  const title = updateOperationTitle(operation, t.operationDock);
  return <Modal title={title} size="sm" presentation="center" onClose={onClose} onClosed={onClosed}
    aria-busy={running || undefined} data-testid="update-operation-progress-dialog">
    <ModalBody gap={4}>
      <div className="flex items-center gap-2 text-ui text-foreground">
        {running ? <Spinner size="sm" /> : null}
        <span role={failed ? 'alert' : 'status'} className="min-w-0 break-words">{label}</span>
      </div>
      {operation.updatesHeld ? <p className="text-ui text-muted-foreground break-words">{t.updateStatus.updatesHeld}</p> : null}
      {operation.error ? <p className="text-ui text-destructive break-words">{operation.error}</p> : null}
      {running ? <Progress aria-label={t.operationProgress.progressLabel} value={null} indicatorValue={100}
        indicatorClassName="operation-progress-indeterminate" /> : null}
    </ModalBody>
    <ModalFooter>
      <Button variant="ghost" onClick={onClose}>{running ? t.operationProgress.minimize : t.common.close}</Button>
    </ModalFooter>
  </Modal>;
}
